import {
  AmpNavFeedbackNotification,
  AmpNavScoreNotification,
} from '@amplify/amp-navigation';
import { useEffect, useState } from 'react';

import hooksConstants from 'src/constants/hooks.constants';
import { NOTIFICATION_STALE_PERIOD_IN_DAYS } from 'src/constants/notifications.constants';
import { useIsMiddleSchoolView } from 'src/hooks/use-is-middle-school-view';
import AuthService from 'src/services/api/auth.service';
import {
  Feedback,
  Score,
  useGetFeedbackQuery,
  useGetScoreQuery,
} from 'src/store/api/insights';
import { getDayJSObject } from 'src/utils/date.utils';
import {
  isDesmosFeedback,
  isNotificationSupportingReadStatus,
} from 'src/utils/feedback.utils';

type Notifications = Array<
  AmpNavFeedbackNotification | AmpNavScoreNotification
>;

const insightsPollingInterval = hooksConstants.INSIGHTS_POLLING_INTERVAL;

function isScore(data: Score | Feedback): data is Score {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  return (data as Score).insight_type === 'teacher-score';
}

const isNewItem = (item: Feedback | Score): boolean => {
  const itemTimestamp = item.updated_timestamp ?? item.created_timestamp;
  const itemCreatedInDays = getDayJSObject().diff(itemTimestamp, 'day');

  if (!isNotificationSupportingReadStatus(itemTimestamp)) {
    return itemCreatedInDays <= NOTIFICATION_STALE_PERIOD_IN_DAYS;
  }

  if (isScore(item)) {
    const isScoreRead = Boolean(item.insight_content.isScoreRead);

    // @TODO: start checking isScoreRead of Desmos Classroom teacher score once Desmos Classroom will start sending isScoreRead
    return isDesmosFeedback(item.insight_content.url)
      ? itemCreatedInDays <= NOTIFICATION_STALE_PERIOD_IN_DAYS
      : !isScoreRead;
  }

  return item.insight_content.unreadCommentCount > 0;
};

const prepareNotifications = (
  data: Array<Score | Feedback>,
  isNewItemCheck: (item: Score | Feedback) => boolean,
): [
  Array<AmpNavScoreNotification | AmpNavFeedbackNotification>,
  Array<AmpNavScoreNotification | AmpNavFeedbackNotification>,
] => {
  const newItems: Notifications = [];
  const prevItems: Notifications = [];

  data.forEach((item) => {
    let notification: AmpNavScoreNotification | AmpNavFeedbackNotification;

    if (isScore(item)) {
      if (!item.insight_content.score) return;

      notification = prepareScoreNotificationData(item);
    } else {
      notification = prepareFeedbackNotificationData(item);
    }

    if (!isScore(item) && !item.insight_content.totalCommentCount) {
      return;
    }

    isNewItemCheck(item)
      ? newItems.push(notification)
      : prevItems.push(notification);
  });

  return [newItems, prevItems];
};

const prepareFeedbackNotificationData = (feedbackData: Feedback) => {
  const unreadCommentCount = feedbackData.insight_content.unreadCommentCount;
  const totalCommentCount = feedbackData.insight_content.totalCommentCount;
  const notificationsCount = isDesmosFeedback(feedbackData.insight_content.url)
    ? unreadCommentCount
    : totalCommentCount;
  const feedbackDate =
    feedbackData.updated_timestamp ?? feedbackData.created_timestamp;

  return {
    id: feedbackData.id,
    notificationsCount,
    thumbnail: feedbackData.insight_content.thumbnailURL,
    title: feedbackData.insight_content.lessonTitle,
    notificationLink: feedbackData.insight_content.url,
    createdDate: feedbackDate,
  };
};

const prepareScoreNotificationData = (scoreData: Score) => {
  const scoreDate = scoreData.updated_timestamp ?? scoreData.created_timestamp;

  return {
    id: scoreData.id,
    createdDate: scoreDate,
    notificationLink: scoreData.insight_content.url,
    thumbnail: scoreData.insight_content.thumbnailURL,
    title: scoreData.insight_content.lessonTitle,
    score: scoreData.insight_content.score,
    scoreType: scoreData.insight_content.scoreType,
  };
};

export const useNavigationNotifications = () => {
  const [newNotifications, setNewNotifications] = useState<Notifications>([]);
  const [previousNotifications, setPreviousNotifications] =
    useState<Notifications>([]);

  const userBk = AuthService.getUserBk();
  const isTeacher = AuthService.isTeacher();
  const isMiddleSchoolView = useIsMiddleSchoolView();

  const { isLoading: isFeedbackLoading, data: feedbackData } =
    useGetFeedbackQuery(userBk, {
      skip: !isMiddleSchoolView || isTeacher,
      pollingInterval: insightsPollingInterval,
    });

  const { isLoading: isScoreLoading, data: scoreData } = useGetScoreQuery(
    userBk,
    {
      skip: !isMiddleSchoolView || isTeacher,
      pollingInterval: insightsPollingInterval,
    },
  );

  const isLoading = isFeedbackLoading || isScoreLoading;

  useEffect(() => {
    if (isLoading || !feedbackData || !scoreData) return;

    const [newFeedbackNotifications, prevFeedbackNotifications] =
      prepareNotifications(feedbackData, isNewItem);
    const [newScoreNotifications, prevScoreNotifications] =
      prepareNotifications(scoreData, isNewItem);

    const newItems = [...newFeedbackNotifications, ...newScoreNotifications];
    const prevItems = [...prevFeedbackNotifications, ...prevScoreNotifications];

    newItems.sort((a, b) => b.createdDate - a.createdDate);
    prevItems.sort((a, b) => b.createdDate - a.createdDate);

    setNewNotifications(newItems);
    setPreviousNotifications(prevItems);
  }, [isLoading, feedbackData, scoreData]);

  return {
    newNotifications,
    previousNotifications,
  };
};
